/**
 * Page-local URL params for DrakeUploadPage's two list-affecting pieces of state that were NOT
 * surviving back-navigation or a refresh: the active tab and the tax year (the latter drives
 * four separate query keys -- checklists, pending review, match candidates, analytics). Ninth
 * adoption of the pattern #78 established (after #97-#102, #104).
 *
 * Every other piece of state on this page -- selectedChecklist, the match/detail dialogs, inline
 * field editing, received-item toggles, delete confirmations, bulk checklist selection -- is tied
 * to one checklist item or one dialog and is deliberately OUT of scope.
 *
 * Conventions (mirroring the other hooks in this sweep):
 * - `tab=<value>` is present only when it differs from the default ('upload'); an unrecognised
 *   value falls back to that default.
 * - `taxYear=<year>` is present only when it differs from the page's own default
 *   (currentYear - 1); an unparseable value falls back to that default, never NaN.
 * - Mutations replace history (never push).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear - 1;

const VALID_TABS = ['upload', 'checklists', 'pending', 'analytics', 'filingHistory'] as const;
type DrakeTab = (typeof VALID_TABS)[number];

export interface DrakeUploadScopeParams {
  tab: DrakeTab;
  taxYear: number;
  setTab: (tab: string) => void;
  setTaxYear: (year: number) => void;
}

export function useDrakeUploadScopeParams(): DrakeUploadScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<DrakeTab>(() => {
    const raw = searchParams.get('tab');
    return raw && (VALID_TABS as readonly string[]).includes(raw) ? (raw as DrakeTab) : 'upload';
  }, [searchParams]);

  const taxYear = useMemo(() => {
    const raw = searchParams.get('taxYear');
    if (!raw) return defaultTaxYear;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : defaultTaxYear;
  }, [searchParams]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setTab = useCallback(
    (next: string) => {
      update((params) => {
        if (next === 'upload') params.delete('tab');
        else params.set('tab', next);
      });
    },
    [update]
  );

  const setTaxYear = useCallback(
    (next: number) => {
      update((params) => {
        if (next === defaultTaxYear) params.delete('taxYear');
        else params.set('taxYear', String(next));
      });
    },
    [update]
  );

  return { tab, taxYear, setTab, setTaxYear };
}
